import * as PIXI from 'pixi.js';
import { Entity } from './Entity';
import { EntityData, Rule } from '../types';
import { GRID_WIDTH, GRID_HEIGHT } from '../constants';

export class EntityManager {
  private entities: Entity[] = [];
  private container: PIXI.Container;

  constructor(container: PIXI.Container) {
    this.container = container;
    // Enable z-index sorting
    this.container.sortableChildren = true;
  }

  public createEntities(data: EntityData[]): void {
    for (const entityData of data) {
      this.entities.push(new Entity(entityData, this.container));
    }
  }

  public getEntity(type: string): Entity | undefined {
    return this.entities.find(e => e.type === type);
  }

  public getEntities(): Entity[] {
    return this.entities;
  }

  public getEntitiesAt(x: number, y: number): Entity[] {
    return this.entities.filter(e => e.x === x && e.y === y);
  }

  public getYouEntities(rules: Rule[]): Entity[] {
    return this.entities.filter(e => this.hasProperty(e, 'YOU', rules));
  }

  public updateZIndices(rules: Rule[]): void {
    // Set z-index: player entities should render on top
    // Also update portal animation state
    this.entities.forEach(entity => {
      if (this.hasProperty(entity, 'YOU', rules)) {
        entity.sprite.zIndex = 100; // Player on top
      } else {
        entity.sprite.zIndex = 1; // Other entities below
      }
      
      // Enable special animation for portals with WIN property
      if (entity.type === 'PORTAL') {
        entity.isWinPortal = this.hasProperty(entity, 'WIN', rules);
      }
    });
  }

  private hasProperty(entity: Entity, property: string, rules: Rule[]): boolean {
    for (const rule of rules) {
      if (rule.noun === entity.type && rule.property === property) {
        return true;
      }
    }
    return false;
  }

  public canMove(entity: Entity, dx: number, dy: number, rules: Rule[]): boolean {
    const newX = entity.x + dx;
    const newY = entity.y + dy;

    if (newX < 0 || newX >= GRID_WIDTH || newY < 0 || newY >= GRID_HEIGHT) return false;

    const canPhase = this.hasProperty(entity, 'PHASE', rules);
    const entitiesAtDest = this.getEntitiesAt(newX, newY);

    for (const dest of entitiesAtDest) {
      if (dest === entity) continue;
      if (this.hasProperty(dest, 'STOP', rules) && !canPhase) return false;
      if (this.hasProperty(dest, 'PUSH', rules)) {
        const pushX = newX + dx;
        const pushY = newY + dy;
        if (pushX < 0 || pushX >= GRID_WIDTH || pushY < 0 || pushY >= GRID_HEIGHT) return false;
        const blocked = this.entities.some(e => 
          e.x === pushX && e.y === pushY && this.hasProperty(e, 'STOP', rules)
        );
        if (blocked) return false;
      }
    }

    return true;
  }

  public moveEntity(entity: Entity, dx: number, dy: number, rules: Rule[]): boolean {
    if (!this.canMove(entity, dx, dy, rules)) return false;

    const newX = entity.x + dx;
    const newY = entity.y + dy;

    const entitiesAtDest = this.getEntitiesAt(newX, newY);
    for (const dest of entitiesAtDest) {
      if (dest === entity) continue;
      if (this.hasProperty(dest, 'PUSH', rules)) {
        dest.moveTo(newX + dx, newY + dy);
      }
    }

    entity.moveTo(newX, newY);
    return true;
  }

  public updateAll(): boolean {
    let allDone = true;
    for (const entity of this.entities) {
      if (!entity.updatePosition()) {
        allDone = false;
      }
    }
    return allDone;
  }

  public updatePhantomTextures(dimension: number): void {
    // Update all PHANTOM entities to use dimension-specific sprites
    this.entities.forEach(entity => {
      if (entity.type === 'PHANTOM') {
        entity.updateTexture(dimension);
      }
    });
  }

  public clear(): void {
    this.entities.forEach(e => e.destroy());
    this.entities = [];
  }
}
